import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { mdlTablePagination } from "../../../models/ui-models/TablePagination";
import { PageHeaderSection } from "../../../components/common/PageHeadSection";
import { Input, Skeleton, Space, Spin } from "antd";
import { SFModal } from "../../../components/elements/SFModal";
import { HotelSelect } from "../../../components/common/HotelSelect";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../../utils/ObjectHelper";
import { mdlOtherAgencyListRequest } from "../../../models/service-models/otherAgency/OtherAgencyListRequest";
import { OtherAgencyService } from "../../../services/OtherAgencyService";
import { mdlOtherAgencyDeleteRequest } from "../../../models/service-models/otherAgency/OtherAgencyDeleteRequest";
import { OtherAgencyTable } from "../../../components/otherAgency/OtherAgencyTable";
import { OtherAgencyDetail } from "../../../components/otherAgency/OtherAgencyDetail";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { HeaderButton } from "../../../models/ui-models/HeaderButton";
import AddMultipleOtherAgency from "../../../components/otherAgency/AddMultipleOtherAgency";
import _ from 'lodash';


export const OtherAgencyDefinition = () => {
    const { t } = useTranslation();
    const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
    const [openMultipleOtherAgenciesModal, setOpenMultipleOtherAgenciesModal] = useState<boolean>(false);
    const [selectedAgency, setSelectedAgency] = useState<string>();
    const [selectedHotel, setSelectedHotel] = useState<string>();
    const [request, setRequest] = useState<mdlOtherAgencyListRequest>({ pageNumber: 1, pageSize: 10 });
    const [search, setSearch] = useState<string>("");

    const debouncedSearch = useCallback(
        _.debounce((value: string) => setSearch(value), 500),
        []
    );

    useEffect(() => {
        if (!data && selectedHotel)
            refetch();
    }, [selectedAgency, request]);


    const {
        data,
        isLoading,
        refetch
    } = useQuery(
        ["getAgencies", selectedHotel, request, search],
        () => {
            if (selectedHotel)
                return OtherAgencyService.List({ ...request, hotelId: selectedHotel, agencyName: search });
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedHotel
        }
    );

    const handleHotelSelect = (hotelId: string) => {
        setRequest({ pageNumber: 1, pageSize: 10 })
        setSelectedHotel(hotelId);
    }

    const handleTableParamsChange = (pagination: mdlTablePagination, filters: any) => {
        setRequest({ ...request, pageSize: pagination.pageSize ?? 10, pageNumber: pagination.current ?? 1 })
    }

    const handleEdit = (agencyId: string) => {
        setSelectedAgency(agencyId);
        setOpenDetailModal(true);
    }

    const handleDelete = (agencyId: string) => {
        deleteAgency({ id: agencyId });
    }

    const { mutateAsync: deleteAgency, isLoading: mutateLoading, data: mutateData } = useMutation(
        (request: mdlOtherAgencyDeleteRequest) => OtherAgencyService.Delete(request),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    toast.success(t("common.success"));
                    refetch();
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const handleAddNew = () => {
        setSelectedAgency(undefined);
        setOpenDetailModal(true);
    }

    const handleDetailCallback = () => {
        setOpenDetailModal(false);
        setOpenMultipleOtherAgenciesModal(false);
        refetch();
    }

    const getButtons = (): HeaderButton[] | undefined => {
        let _buttons: HeaderButton[] = [];
        _buttons.push({
            title: t("definitions.otherAgencies.addNew"),
            icon: faPlus,
            fnFunction: handleAddNew,
            className: "btn btn-white btn-dim btn-outline-primary"
        });
        _buttons.push({
            title: t("definitions.otherAgencies.addMultipleOtherAgencies"),
            icon: faPlus,
            fnFunction: () => setOpenMultipleOtherAgenciesModal(true),
            className: "btn btn-white btn-dim btn-outline-primary"
        });

        return _buttons;
    }
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSearch(e.target.value);
    };
    return (
        <Spin spinning={mutateLoading}>
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <PageHeaderSection
                                pageTitle={t("definitions.otherAgencies.title")}
                                buttons={selectedHotel ? getButtons() : undefined}
                            />
                            <div className="nk-block">
                                <div className="row g-gs">
                                    <div className="col-12">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">
                                                {
                                                    <HotelSelect onChange={handleHotelSelect} />
                                                }
                                                {selectedHotel && (

                                                    <>
                                                        <hr />
                                                        <Space align="center" style={{ justifyContent: "flex-end", display: "flex" }}>
                                                            <Input.Search
                                                                placeholder={t("agencies.name")}
                                                                onChange={handleSearchChange}
                                                            />
                                                        </Space>
                                                        <hr />
                                                    </>
                                                )}
                                                {
                                                    isLoading
                                                    &&
                                                    <>
                                                        <Skeleton active />
                                                        <Skeleton active />
                                                        <Skeleton active />
                                                    </>
                                                }
                                                {
                                                    data
                                                    &&
                                                    <OtherAgencyTable
                                                        onDelete={handleDelete}
                                                        onEdit={handleEdit}
                                                        pagination={{
                                                            align: "center",
                                                            current: data?.body?.pageNumber,
                                                            pageSizeOptions: [10, 20, 50],
                                                            defaultPageSize: 10,
                                                            pageSize: request.pageSize,
                                                            defaultCurrent: 1,
                                                            responsive: true,
                                                            showSizeChanger: true,
                                                            total: data?.body?.resultCount,
                                                            showTotal: (total: number, range: [number, number]) => t("pagination.total") + ` ${data?.body?.resultCount} ` + t("pagination.result"),
                                                            locale: { items_per_page: "/ " + t("pagination.itemPerPage") }
                                                        }}
                                                        onTableParamsChange={handleTableParamsChange}
                                                        dataSource={data?.body?.data} />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openDetailModal}
                toggler={() => { setOpenDetailModal(!openDetailModal) }}
                body={<OtherAgencyDetail hotelId={selectedHotel} id={selectedAgency} callback={handleDetailCallback} />}
                title={t("definitions.otherAgencies.detailTitle")}
            />
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openMultipleOtherAgenciesModal}
                toggler={() => { setOpenMultipleOtherAgenciesModal(!openMultipleOtherAgenciesModal) }}
                body={<AddMultipleOtherAgency callback={handleDetailCallback} />}
                title={t("definitions.otherAgencies.addMultipleOtherAgencies")}
            />
        </Spin>
    );
};
